import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Customer } from "../../../../../api/customers";
import type { InvoiceCustomerPayload } from "../../../../../api/invoices";
import { useCustomers } from "../../../../../hooks/useCustomers";
import { isValidBgVat, isValidEIK } from "../../../../../lib/companyValidators";
import type { EikLookupFields } from "../../../../../lib/eikLookupFields";

/** Полетата на купувача, както са въведени (непочистени). */
export interface InvoiceBuyerValues {
  eik: string;
  vat: string;
  name: string;
  city: string;
  address: string;
  mol: string;
}

/**
 * Грешките по поле — ключовете са тези на `InvoiceCreateModal` (по тях
 * полето светва в червено), редът им е редът на първото предупреждение.
 * МОЛ е единственото незадължително поле; ДДС № се проверява само попълнен.
 */
export const invoiceBuyerErrorsOf = (
  values: InvoiceBuyerValues,
  t: TFunction,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!values.eik.trim() || !isValidEIK(values.eik.trim())) {
    errors.customer_eik = t("invoiceCreate.errCustomerEik");
  }
  if (values.vat.trim() && !isValidBgVat(values.vat.trim())) {
    errors.customer_vat = t("invoiceCreate.errCustomerVat");
  }
  if (!values.name.trim()) errors.customer_name = t("invoiceCreate.errCustomerFields");
  if (!values.city.trim()) errors.customer_city = t("invoiceCreate.errCustomerFields");
  if (!values.address.trim()) errors.customer_address = t("invoiceCreate.errCustomerFields");
  return errors;
};

/** Тялото `customer` (BE `InvoiceCustomerIn`) — почистено, празните незадължителни са null. */
export const invoiceCustomerPayloadOf = (values: InvoiceBuyerValues): InvoiceCustomerPayload => ({
  eik: values.eik.trim(),
  vat_number: values.vat.trim() || null,
  company_name: values.name.trim(),
  city: values.city.trim(),
  address: values.address.trim(),
  mol: values.mol.trim() || null,
});

const EMPTY_VALUES: InvoiceBuyerValues = {
  eik: "",
  vat: "",
  name: "",
  city: "",
  address: "",
  mol: "",
};

/** С какво започва отварянето вместо празно — купувачът на отказано
 *  закриване (`refusedFiscalInvoiceBuyers.ts`). */
export interface InvoiceBuyerDraft {
  values: InvoiceBuyerValues;
  receivedBy: string;
}

/**
 * Купувачът на фактура — общ за издаването (`InvoiceCreateModal`) и за
 * фискалната фактура при закриване (`FiscalInvoiceBuyerModal`). Полетата се
 * пълнят по три начина: ръчно; ЕИК + иконата (VIES / Търговски регистър);
 * „Търсене в издадените фактури" (вече записан клиент). „Получил" е тук,
 * защото следва фирмата: изборът на клиент зарежда последното му лице, а нова
 * фирма от регистъра го чисти. Всяко отваряне започва празно — или с `initial`
 * (трябва да е стабилен между рендерите).
 */
export function useInvoiceBuyer({
  open,
  initial = null,
}: {
  open: boolean;
  initial?: InvoiceBuyerDraft | null;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<InvoiceBuyerValues>(initial?.values ?? EMPTY_VALUES);
  const [receivedBy, setReceivedBy] = useState(initial?.receivedBy ?? "");
  const [pickedCustomerId, setPickedCustomerId] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const { data: customerOptions = [] } = useCustomers(customerSearch, { enabled: open });

  useEffect(() => {
    if (!open) return;
    setValues(initial?.values ?? EMPTY_VALUES);
    setReceivedBy(initial?.receivedBy ?? "");
    setPickedCustomerId(null);
    setCustomerSearch("");
  }, [open, initial]);

  /** Ръчна промяна на поле — вече не е избраният клиент. */
  const setField = (field: keyof InvoiceBuyerValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setPickedCustomerId(null);
  };

  const fillFromCustomer = (customer: Customer) => {
    setPickedCustomerId(customer.id);
    setValues({
      eik: customer.eik,
      vat: customer.vat_number ?? "",
      name: customer.company_name,
      city: customer.city,
      address: customer.address,
      mol: customer.mol ?? "",
    });
    setReceivedBy(customer.last_received_by ?? "");
  };

  // Иконата до „ЕИК": новата фирма заменя всички полета — и „Получил",
  // който е последният получател на предишната фирма.
  const fillFromLookup = (found: EikLookupFields) => {
    setValues((current) => ({
      ...current,
      vat: found.vatNumber,
      name: found.companyName,
      city: found.city,
      address: found.address,
      mol: found.mol,
    }));
    setReceivedBy("");
    setPickedCustomerId(null);
  };

  const errors = useMemo(() => invoiceBuyerErrorsOf(values, t), [values, t]);

  return {
    values,
    setField,
    receivedBy,
    setReceivedBy,
    pickedCustomerId,
    clearPickedCustomer: () => setPickedCustomerId(null),
    customerSearch,
    setCustomerSearch,
    customerOptions,
    fillFromCustomer,
    fillFromLookup,
    errors,
    toCustomerPayload: () => invoiceCustomerPayloadOf(values),
  };
}

export type InvoiceBuyer = ReturnType<typeof useInvoiceBuyer>;
